import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../../utils';
import { applyTreatmentPlanV3AssessmentPageFields } from '../utils';
import {
  isForLoadedAssessmentPage,
  isForLoadedAssessmentTemplates,
  markAssessmentPageChanged,
} from './utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { TreatmentPlanV3State } from '../../../types';
import type {
  AssessmentMutationAction,
  ITreatmentPlanV3AssessmentPage,
  TreatmentPlanV3AssessmentThunks,
  UpdateTreatmentPlanV3AssessmentPagePayload,
} from '../types';

export function addGetTreatmentPlanV3AssessmentPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { getTreatmentPlanV3AssessmentPage } = thunks;

  builder
    .addCase(getTreatmentPlanV3AssessmentPage.pending, (state, action) => {
      if (!isForLoadedAssessmentPage(state, action)) {
        state.assessmentPage = null;
      }
      state.assessmentPageLoading = true;
      state.assessmentPageError = null;
      state.assessmentPageRequestId = action.meta.requestId;
      state.assessmentPageTreatmentPlanId = action.meta.arg.treatmentPlanId;
      state.assessmentPageChangedSinceRequest = false;
      state.error = null;
    })
    .addCase(getTreatmentPlanV3AssessmentPage.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.assessmentPageRequestId) return;
      state.assessmentPageLoading = false;
      state.assessmentPageError = null;
      if (!state.assessmentPageChangedSinceRequest || !state.assessmentPage) {
        state.assessmentPage = action.payload;
      }
      state.assessmentPageChangedSinceRequest = false;
    })
    .addCase(getTreatmentPlanV3AssessmentPage.rejected, (state, action) => {
      if (action.meta.requestId !== state.assessmentPageRequestId) return;
      state.assessmentPageLoading = false;
      state.assessmentPageError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

export function addGetTreatmentPlanV3AssessmentTemplatesCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { getTreatmentPlanV3AssessmentTemplates } = thunks;

  builder
    .addCase(getTreatmentPlanV3AssessmentTemplates.pending, (state, action) => {
      if (!isForLoadedAssessmentTemplates(state, action)) {
        state.assessmentTemplates = null;
      }
      state.assessmentTemplatesLoading = true;
      state.assessmentTemplatesError = null;
      state.assessmentTemplatesRequestId = action.meta.requestId;
      state.assessmentTemplatesTreatmentPlanId = action.meta.arg.treatmentPlanId;
    })
    .addCase(getTreatmentPlanV3AssessmentTemplates.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.assessmentTemplatesRequestId) return;
      state.assessmentTemplatesLoading = false;
      state.assessmentTemplatesError = null;
      state.assessmentTemplates = action.payload;
    })
    .addCase(getTreatmentPlanV3AssessmentTemplates.rejected, (state, action) => {
      if (action.meta.requestId !== state.assessmentTemplatesRequestId) return;
      state.assessmentTemplatesLoading = false;
      state.assessmentTemplatesError = action.error;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

export function addUpdateTreatmentPlanV3AssessmentPageCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { updateTreatmentPlanV3AssessmentPage } = thunks;
  const flag = 'updateAssessmentPageLoading';

  builder
    .addCase(updateTreatmentPlanV3AssessmentPage.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3AssessmentPage.fulfilled,
      (
        state,
        action: AssessmentMutationAction<
          ITreatmentPlanV3AssessmentPage,
          UpdateTreatmentPlanV3AssessmentPagePayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action)) return;
        markAssessmentPageChanged(state);
        if (!state.assessmentPage) {
          state.assessmentPage = action.payload;
          return;
        }
        state.assessmentPage.id = action.payload.id;
        applyTreatmentPlanV3AssessmentPageFields(
          state.assessmentPage,
          action.payload,
          action.meta.arg.data,
        );
        if (action.payload.readiness_scores) {
          state.assessmentPage.readiness_scores = action.payload.readiness_scores;
        }
      },
    )
    .addCase(updateTreatmentPlanV3AssessmentPage.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}
